import { TFunction } from "i18next";

import {
	ReceiptStatus,
	ReceiptSummary,
} from "../services/ReceiptProvider/types";

const tBase = "orderPageWidgets.orderDetails.receipt";

export enum ReceiptDisplayKind {
	NotApplicable = "notApplicable",
	Skipped = "skipped",
	Failed = "failed",
	Success = "success",
}

export const RECEIPT_DISPLAY_COLORS: Record<ReceiptDisplayKind, string> = {
	[ReceiptDisplayKind.NotApplicable]: "#888888",
	[ReceiptDisplayKind.Skipped]: "#888888",
	[ReceiptDisplayKind.Failed]: "#d04141",
	[ReceiptDisplayKind.Success]: "#3b9b3b",
};

export function resolveReceiptDisplayKind(
	status?: ReceiptStatus | null,
): ReceiptDisplayKind {
	if (status === ReceiptStatus.SUCCESS) {
		return ReceiptDisplayKind.Success;
	}

	if (status === ReceiptStatus.FAILED) {
		return ReceiptDisplayKind.Failed;
	}

	if (status === ReceiptStatus.SKIPPED) {
		return ReceiptDisplayKind.Skipped;
	}

	return ReceiptDisplayKind.NotApplicable;
}

export type ReceiptLike =
	| {
			status?: ReceiptStatus | null;
			skipReason?: string | null;
	  }
	| null
	| undefined;

export function resolveReceiptDisplayLabel(
	t: TFunction,
	receipt?: ReceiptLike,
): string {
	const status = receipt?.status;

	if (!status) {
		return t(`${tBase}.notApplicable`) ?? "—";
	}

	const kind = resolveReceiptDisplayKind(status);

	if (kind === ReceiptDisplayKind.Success) {
		return t(`${tBase}.statuses.SUCCESS`) ?? status;
	}

	if (kind === ReceiptDisplayKind.Failed) {
		return t(`${tBase}.statuses.FAILED`) ?? status;
	}

	if (status === ReceiptStatus.SKIPPED) {
		return t(`${tBase}.statuses.SKIPPED`) ?? status;
	}

	if (status === ReceiptStatus.PENDING) {
		return t(`${tBase}.statuses.PENDING`) ?? status;
	}

	return t(`${tBase}.statuses.${status}`) ?? status;
}

export function resolveReceiptCellDisplay(
	receipt?: ReceiptSummary | null,
	t?: TFunction,
): {
	content: string;
	backgroundColor?: string;
	kind: ReceiptDisplayKind;
} {
	const kind = resolveReceiptDisplayKind(receipt?.status);

	if (!receipt?.status) {
		return {
			content: "",
			backgroundColor: undefined,
			kind,
		};
	}

	return {
		content: t ? resolveReceiptDisplayLabel(t, receipt) : receipt.status,
		backgroundColor: RECEIPT_DISPLAY_COLORS[kind],
		kind,
	};
}

export function canRetryReceipt(status?: ReceiptStatus | null): boolean {
	return status === ReceiptStatus.FAILED;
}

export function isReceiptStatusInProgress(
	status?: ReceiptStatus | null,
): boolean {
	return status === ReceiptStatus.PENDING;
}

export function isReceiptStatusTerminal(
	status?: ReceiptStatus | null,
): boolean {
	return (
		status === ReceiptStatus.SUCCESS ||
		status === ReceiptStatus.FAILED ||
		status === ReceiptStatus.SKIPPED
	);
}

export function canPreviewReceiptSummary(
	receipt?: ReceiptSummary | null,
): boolean {
	return (
		receipt?.status === ReceiptStatus.SUCCESS &&
		!!receipt?.transactionId &&
		!!receipt?.externalId
	);
}

/** Нижняя панель заказа: показываем чек только для финальных исходов. */
export function shouldShowReceiptInOrderBottom(
	status?: ReceiptStatus | null,
): boolean {
	return status === ReceiptStatus.SUCCESS || status === ReceiptStatus.FAILED;
}
